import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { Button } from '../atoms';

/**
 * SignaturePad — a dependency-free HTML5 canvas signature capture.
 *
 * The user draws with a mouse, pen, or finger (unified pointer events); `Clear`
 * wipes the canvas and `Save signature` exports the drawing as a PNG data URL via
 * `canvas.toDataURL('image/png')`, handed back through `onSave`. The canvas is
 * responsive (fills its container width) and crisp on HiDPI screens (backing store
 * sized to devicePixelRatio). `touch-action: none` keeps a finger stroke from
 * scrolling the page while signing.
 *
 * No external library — the pad is small enough to hand-roll, which avoids adding
 * a dependency to the shared install.
 */
export interface SignaturePadProps {
  /** Called with the PNG data URL when the user saves a non-empty drawing. */
  onSave: (dataUrl: string) => void;
  /** Show the Save button in a loading state (e.g. while the POST is in flight). */
  saving?: boolean;
  /** Canvas height in CSS pixels (width is always 100% of the container). */
  height?: number;
  /** Stroke color. Defaults to a near-black ink. */
  penColor?: string;
  /** Accessible label for the drawing surface. */
  ariaLabel?: string;
}

export function SignaturePad({
  onSave,
  saving = false,
  height = 180,
  penColor = '#111111',
  ariaLabel = 'Signature drawing area',
}: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  const [hasInk, setHasInk] = useState(false);

  // Size the backing store to the displayed size × devicePixelRatio and reset the
  // drawing context. Called on mount and on resize (a resize wipes the drawing,
  // which is acceptable — resizing mid-signature is rare).
  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
    const cssWidth = canvas.clientWidth || 320;
    canvas.width = Math.max(1, Math.floor(cssWidth * ratio));
    canvas.height = Math.max(1, Math.floor(height * ratio));
    const ctx = canvas.getContext('2d');
    ctxRef.current = ctx;
    if (ctx) {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(ratio, ratio);
      ctx.lineWidth = 2.2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = penColor;
    }
  }, [height, penColor]);

  useEffect(() => {
    setupCanvas();
    const onResize = () => {
      setupCanvas();
      setHasInk(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [setupCanvas]);

  const pointFromEvent = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handlePointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture?.(e.pointerId);
    drawing.current = true;
    const p = pointFromEvent(e);
    last.current = p;
    // A single tap registers a dot so a tap-only "signature" still saves.
    const ctx = ctxRef.current;
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
      ctx.lineTo(p.x + 0.01, p.y + 0.01);
      ctx.stroke();
    }
    setHasInk(true);
  };

  const handlePointerMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = ctxRef.current;
    const p = pointFromEvent(e);
    if (ctx && last.current) {
      ctx.beginPath();
      ctx.moveTo(last.current.x, last.current.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
    }
    last.current = p;
  };

  const endStroke = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    drawing.current = false;
    last.current = null;
    canvasRef.current?.releasePointerCapture?.(e.pointerId);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
  };

  const save = () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasInk) return;
    onSave(canvas.toDataURL('image/png'));
  };

  return (
    <div className="signature-pad">
      <canvas
        ref={canvasRef}
        aria-label={ariaLabel}
        role="img"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endStroke}
        onPointerLeave={endStroke}
        onPointerCancel={endStroke}
        style={{
          display: 'block',
          width: '100%',
          height,
          touchAction: 'none',
          cursor: 'crosshair',
          border: '1px solid var(--color-border, #e2e2df)',
          borderRadius: 8,
          background: 'var(--color-surface-raised, #fff)',
        }}
      />
      <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
        <Button size="sm" variant="secondary" onClick={clear} disabled={!hasInk || saving}>
          Clear
        </Button>
        <Button size="sm" onClick={save} disabled={!hasInk} loading={saving}>
          Save signature
        </Button>
        {!hasInk && (
          <span style={{ fontSize: 12, color: 'var(--color-muted, #666)' }}>Draw your signature above.</span>
        )}
      </div>
    </div>
  );
}
